import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";

const EMPTY = {
  name: "", code: "", location: "", google_maps: "", project_type: "residential",
  ownership_type: "owned", rera_number: "", land_area_sqft: 0, construction_area_sqft: 0,
  fsi: 0, building_config: "", estimated_cost: 0, estimated_revenue: 0,
  number_of_buildings: 0, number_of_towers: 0, number_of_wings: 0, total_floors: 0,
  number_of_flats: 0, number_of_row_houses: 0, number_of_shops: 0,
  number_of_offices: 0, number_of_showrooms: 0, unit_configuration: "",
  start_date: "", completion_date: "", project_manager: "", architect: "",
  structural_consultant: "", legal_consultant: "",
  thumbnail_url: "https://images.pexels.com/photos/18153132/pexels-photo-18153132.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  description: "",
};

const NUMERIC_KEYS = [
  "land_area_sqft", "construction_area_sqft", "fsi",
  "estimated_cost", "estimated_revenue",
  "number_of_buildings", "number_of_towers", "number_of_wings", "total_floors",
  "number_of_flats", "number_of_row_houses", "number_of_shops",
  "number_of_offices", "number_of_showrooms",
];

export default function ProjectNew() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function onSubmit(e) {
    e.preventDefault();
    if (!form.name || !form.code || !form.location) {
      toast.error("Name, code and location are required");
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      for (const k of NUMERIC_KEYS) payload[k] = Number(payload[k]) || 0;
      const { data } = await api.post("/projects", payload);
      toast.success("Project created");
      nav(`/projects/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create project");
    } finally {
      setBusy(false);
    }
  }

  const field = (id, label, type = "text", numeric = false) => (
    <div>
      <Label htmlFor={id} className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</Label>
      <Input
        id={id} type={type}
        value={form[id]}
        onChange={(e) => set(id, numeric ? e.target.value : e.target.value)}
        className="mt-1 h-10 border-zinc-300"
        data-testid={`project-field-${id}`}
      />
    </div>
  );

  return (
    <div data-testid="project-new-page" className="space-y-6 max-w-5xl">
      <div>
        <div className="text-[11px] uppercase tracking-[0.15em] font-semibold text-emerald-700">New Project</div>
        <h1 className="font-heading text-3xl font-bold mt-1">Create a project</h1>
        <p className="text-sm text-zinc-500 mt-1">Every new project automatically gets its own ledger, inventory, customers and dashboards.</p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <Card className="bg-white border-zinc-200">
          <CardHeader><CardTitle className="font-heading text-base">Basic Details</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {field("name", "Project Name")}
            {field("code", "Project Code")}
            {field("location", "Location")}
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Project Type</Label>
              <Select value={form.project_type} onValueChange={(v) => set("project_type", v)}>
                <SelectTrigger className="mt-1 h-10 border-zinc-300" data-testid="project-field-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="residential">Residential</SelectItem>
                  <SelectItem value="row_house">Row House</SelectItem>
                  <SelectItem value="commercial">Commercial</SelectItem>
                  <SelectItem value="mixed">Mixed-use</SelectItem>
                  <SelectItem value="land_development">Land Development</SelectItem>
                  <SelectItem value="joint_development">Joint Development</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-widest text-zinc-500">Ownership</Label>
              <Select value={form.ownership_type} onValueChange={(v) => set("ownership_type", v)}>
                <SelectTrigger className="mt-1 h-10 border-zinc-300" data-testid="project-field-ownership"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="owned">Company Owned</SelectItem>
                  <SelectItem value="jda">Joint Development (JDA)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {field("rera_number", "RERA Number")}
            {field("google_maps", "Google Maps URL")}
            {field("thumbnail_url", "Thumbnail URL")}
          </CardContent>
        </Card>

        <Card className="bg-white border-zinc-200">
          <CardHeader><CardTitle className="font-heading text-base">Sizing & Financials</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {field("land_area_sqft", "Land Area (sqft)", "number", true)}
            {field("construction_area_sqft", "Construction Area (sqft)", "number", true)}
            {field("fsi", "FSI", "number", true)}
            {field("estimated_cost", "Estimated Cost (₹)", "number", true)}
            {field("estimated_revenue", "Estimated Revenue (₹)", "number", true)}
            {field("building_config", "Building Configuration Notes")}
          </CardContent>
        </Card>

        <Card className="bg-white border-zinc-200">
          <CardHeader>
            <CardTitle className="font-heading text-base">Project Configuration</CardTitle>
            <p className="text-[11px] text-zinc-500 mt-1">
              Structure counts — the Interactive Sales Board will auto-scale layout using towers × floors × units.
            </p>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {field("number_of_buildings", "Buildings", "number", true)}
            {field("number_of_towers", "Towers", "number", true)}
            {field("number_of_wings", "Wings", "number", true)}
            {field("total_floors", "Total Floors (per tower)", "number", true)}
            {field("number_of_flats", "Flats", "number", true)}
            {field("number_of_row_houses", "Row Houses", "number", true)}
            {field("number_of_shops", "Shops", "number", true)}
            {field("number_of_offices", "Offices", "number", true)}
            {field("number_of_showrooms", "Showrooms", "number", true)}
            <div className="col-span-2 md:col-span-3">
              {field("unit_configuration", "Unit Configuration (e.g., 2 & 3 BHK, 1050-1450 sqft)")}
            </div>
          </CardContent>
        </Card>

        <Card className="bg-white border-zinc-200">
          <CardHeader><CardTitle className="font-heading text-base">Timeline & Team</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {field("start_date", "Start Date", "date")}
            {field("completion_date", "Completion Date", "date")}
            {field("project_manager", "Project Manager")}
            {field("architect", "Architect")}
            {field("structural_consultant", "Structural Consultant")}
            {field("legal_consultant", "Legal Consultant")}
          </CardContent>
        </Card>

        <Card className="bg-white border-zinc-200">
          <CardHeader><CardTitle className="font-heading text-base">Description</CardTitle></CardHeader>
          <CardContent>
            <Textarea
              value={form.description}
              onChange={(e) => set("description", e.target.value)}
              rows={4}
              className="border-zinc-300"
              data-testid="project-field-description"
            />
          </CardContent>
        </Card>

        <div className="flex gap-3 justify-end">
          <Button type="button" variant="outline" onClick={() => nav("/projects")} data-testid="cancel-btn">Cancel</Button>
          <Button type="submit" disabled={busy} className="bg-emerald-700 hover:bg-emerald-800" data-testid="save-project-btn">
            {busy ? "Creating…" : "Create Project"}
          </Button>
        </div>
      </form>
    </div>
  );
}
